import { Button } from '@client/components/ui/button';
import { formatCurrency, formatPercent } from '@shared';
import { Pencil } from 'lucide-react';
import { useMemo } from 'react';
import { useLtcPlanningCalculations } from './hooks/useLtcPlanningCalculations';

interface AccountDetailSummaryProps {
  selfFundingSource: SelfFundingSource;
  onEdit: () => void;
  onNext?: () => void;
  showNextButton?: boolean;
  nextButtonLabel?: string;
  deleteButton?: React.ReactNode;
}

export function AccountDetailSummary({
  selfFundingSource,
  onEdit,
  deleteButton,
}: AccountDetailSummaryProps) {
  const { calculateSelfFunding } = useLtcPlanningCalculations();

  const { projectedValue } = useMemo(
    () => calculateSelfFunding(selfFundingSource),
    [calculateSelfFunding, selfFundingSource],
  );

  const summaryItems = [
    {
      label: 'Current Value',
      value: formatCurrency(selfFundingSource.existingAssetsValue || 0),
    },
    {
      label: 'Monthly Contribution',
      value: formatCurrency(selfFundingSource.monthlyContributionAmount || 0),
    },
    {
      label: 'Annual Return',
      value: formatPercent(selfFundingSource.annualRateOfReturn || 0, 2),
    },
    {
      label: 'Gains Tax Rate',
      value: formatPercent(selfFundingSource.gainsTaxRate || 0, 2),
    },
    {
      label: 'Projected Value',
      value: formatCurrency(projectedValue),
    },
  ];

  return (
    <div className="flex flex-col items-center gap-y-6">
      <div className="w-full space-y-4">
        {summaryItems.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between"
          >
            <span className="text-sm text-gray-600">{item.label}</span>
            <span className="text-sm font-semibold text-gray-900">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        {deleteButton}
        <Button
          onClick={onEdit}
          variant="outline"
          className="rounded-full"
          size="sm"
        >
          <Pencil className="h-4 w-4" />
          Edit
        </Button>
      </div>
    </div>
  );
}
